import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Plus,
  Search,
  Edit,
  Eye,
  Trash2,
  Send,
  Sparkles,
  MoreVertical,
  Loader2,
  X,
  Target,
  CheckCircle2,
  Wand2,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const MD_KEYS = ['content_markdown', 'article_content', 'content', 'body', 'article', 'text', 'markdown'];

function unwrapMarkdown(raw) {
  if (!raw || typeof raw !== 'string') return raw || '';
  let text = raw.trim();
  if (text.startsWith('```json')) {
    text = text.split('```json')[1]?.split('```')[0]?.trim() || text;
  } else if (text.startsWith('```')) {
    text = text.split('```')[1]?.split('```')[0]?.trim() || text;
  }
  if (text[0] !== '[' && text[0] !== '{') return raw;
  try {
    let parsed = JSON.parse(text);
    if (Array.isArray(parsed) && parsed.length > 0) parsed = parsed[0];
    if (parsed && typeof parsed === 'object') {
      for (const key of MD_KEYS) {
        const val = parsed[key];
        if (val && typeof val === 'string' && val.length > 100) return val;
      }
    }
  } catch { /* not JSON, return as-is */ }
  return raw;
}

const PIPELINE_STEPS = [
  { key: 'draft',              label: 'Draft',    statuses: ['draft', 'ai_generated'] },
  { key: 'humanized',          label: 'Humanize', statuses: ['humanized'] },
  { key: 'ready_for_approval', label: 'Review',   statuses: ['ready_for_approval'] },
  { key: 'approved',           label: 'Approve',  statuses: ['approved'] },
  { key: 'published',          label: 'Publish',  statuses: ['published'] },
];

function PipelineBar({ status }) {
  const currentIdx = PIPELINE_STEPS.findIndex(s => s.statuses.includes(status));
  const isRejected = status === 'rejected';

  return (
    <div className="flex items-center gap-1">
      {PIPELINE_STEPS.map((step, i) => {
        const isActive = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <div key={step.key} className="flex items-center gap-1">
            <div className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
              isRejected && i === 0 ? 'bg-red-100 text-red-700' :
              isActive && step.key === 'published' ? 'bg-emerald-100 text-emerald-700' :
              isActive ? 'bg-indigo-100 text-indigo-700' :
              isDone ? 'bg-emerald-100 text-emerald-700' :
              'bg-slate-100 text-slate-400'
            }`}>
              {step.label}
            </div>
            {i < PIPELINE_STEPS.length - 1 && (
              <ArrowRight className={`w-3 h-3 ${isDone || (isActive && step.key === 'published') ? 'text-emerald-400' : 'text-slate-300'}`} />
            )}
          </div>
        );
      })}
      {isRejected && (
        <Badge className="bg-red-100 text-red-700 ml-1">Rejected</Badge>
      )}
    </div>
  );
}

export default function ContentDashboard() {
  const { selectedProjectId } = useProject();
  const [content, setContent] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const generateTask = useTaskState('content-generate');
  const humanizeTask = useTaskState('content-humanize');

  const generating = generateTask.running;
  /** Which row or panel is humanizing: content_id, 'create-dialog', or null */
  const [humanizingId, setHumanizingId] = useState(null);

  const [saving, setSaving] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);

  // Restore generated content from TaskStateContext when navigating back to this page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (generateTask.result) setGeneratedContent(generateTask.result); }, [generateTask.result]);
  const [humanizedResult, setHumanizedResult] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [exampleIdx, setExampleIdx] = useState(0);
  const [newContent, setNewContent] = useState({
    title: '',
    content_type: 'blog',
    target_keywords: '',
    brief: ''
  });

  const DUMMY_EXAMPLES = [
    {
      title: '10 Proven SEO Strategies to Rank #1 on Google in 2026',
      content_type: 'blog',
      target_keywords: 'SEO strategies, rank on Google, search engine optimization, keyword ranking',
      brief: 'Cover the top 10 actionable SEO tactics for 2026. Include sections on: Core Web Vitals optimization, E-E-A-T signals, AI-generated content guidelines, zero-click search, topical authority, internal linking, featured snippets, and local SEO. Each section should have a practical tip and a quick win.',
    },
    {
      title: 'How Digital Marketing Transforms Small Business Growth',
      content_type: 'article',
      target_keywords: 'digital marketing, small business growth, online marketing, marketing ROI',
      brief: 'An in-depth article for small business owners explaining how digital marketing channels (SEO, social, email, paid) compare to traditional marketing, with real ROI stats and a beginner-friendly action plan.',
    },
    {
      title: 'Professional SEO Services for Local Businesses',
      content_type: 'landing_page',
      target_keywords: 'SEO services, local SEO, professional SEO agency, rank higher Google',
      brief: 'Landing page for an SEO service agency. Include hero headline, value proposition, 3 service tiers (DIY / Managed / Corporate), client results section, FAQ, and a strong CTA for a free audit.',
    },
  ];

  useEffect(() => {
    fetchContent();
  }, [page]);

  const fetchContent = async () => {
    try {
      const response = await api.get(`/content/?page=${page}&page_size=${pageSize}`);
      setContent(response.data.items || []);
      setTotal(response.data.total || 0);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700' },
      ai_generated: { label: 'AI Generated', color: 'bg-violet-100 text-violet-700' },
      humanized: { label: 'Humanized', color: 'bg-purple-100 text-purple-700' },
      ready_for_approval: { label: 'In Review', color: 'bg-amber-100 text-amber-700' },
      approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700' },
      published: { label: 'Published', color: 'bg-emerald-100 text-emerald-700' },
      rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
    };
    return statuses[status] || statuses.draft;
  };

  const getTypeBadge = (type) => {
    const types = {
      blog: { label: 'Blog', color: 'bg-indigo-100 text-indigo-700' },
      article: { label: 'Article', color: 'bg-violet-100 text-violet-700' },
      landing_page: { label: 'Landing Page', color: 'bg-cyan-100 text-cyan-700' },
      gbp_post: { label: 'GBP Post', color: 'bg-emerald-100 text-emerald-700' },
    };
    return types[type] || types.blog;
  };

  const filteredContent = content.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = selectedTab === 'all' || c.status === selectedTab;
    return matchesSearch && matchesTab;
  });

  const handleCreateContent = async () => {
    if (!newContent.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!newContent.target_keywords.trim()) {
      toast.error('Target keywords are required');
      return;
    }

    try {
      const response = await api.post('/content', {
        project_id: selectedProjectId || undefined,
        title: newContent.title,
        content_type: newContent.content_type,
        target_keywords: newContent.target_keywords.split(',').map(k => k.trim()),
        brief: newContent.brief
      });
      setContent([response.data, ...content]);
      setCreateDialogOpen(false);
      setNewContent({ title: '', content_type: 'blog', target_keywords: '', brief: '' });
      toast.success('Content created successfully');
    } catch (error) {
      toast.error('Failed to create content');
    }
  };

  const handleGenerateAIContent = async () => {
    if (!newContent.title.trim() || !newContent.target_keywords.trim()) {
      toast.error('Title and target keywords are required for AI generation');
      return;
    }

    generateTask.start();
    try {
      const response = await api.post('/ai/generate-content', {
        title: newContent.title,
        target_keywords: newContent.target_keywords.split(',').map(k => k.trim()),
        content_type: newContent.content_type
      });
      if (response.data.error) {
        toast.error(response.data.error);
        generateTask.fail(response.data.error);
        return;
      }
      setGeneratedContent(response.data);
      toast.success('AI content generated successfully');
      generateTask.complete(response.data);
    } catch (error) {
      toast.error('Failed to generate AI content');
      generateTask.fail(error);
    }
  };

  const handleSaveGeneratedContent = async () => {
    if (!generatedContent) return;
    setSaving(true);
    try {
      const cleanContent = unwrapMarkdown(generatedContent.content);
      const response = await api.post('/content', {
        project_id: selectedProjectId || undefined,
        title: newContent.title,
        content_type: newContent.content_type,
        target_keywords: newContent.target_keywords.split(',').map(k => k.trim()),
        brief: newContent.brief || cleanContent,
        draft: cleanContent,
        seo_score: generatedContent.seo_score
      });
      const savedItem = response.data;
      if (generatedContent.was_humanized) {
        try {
          await api.put(`/content/${savedItem.content_id}/`, {
            draft: cleanContent,
            status: 'humanized',
          });
          savedItem.status = 'humanized';
          savedItem.draft = cleanContent;
        } catch {
          // Non-blocking: content is saved, just status didn't update
        }
      }
      setContent([savedItem, ...content]);
      setCreateDialogOpen(false);
      setNewContent({ title: '', content_type: 'blog', target_keywords: '', brief: '' });
      setGeneratedContent(null);
      generateTask.set({ result: null });
      toast.success('Content saved successfully');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleViewContent = (item) => {
    setSelectedContent(item);
    setViewDialogOpen(true);
  };

  const handleEditContent = (item) => {
    setSelectedContent(item);
    setEditDialogOpen(true);
  };

  const handleUpdateContent = async () => {
    if (!selectedContent) return;
    
    try {
      await api.put(`/content/${selectedContent.content_id}/`, {
        title: selectedContent.title,
        brief: selectedContent.brief,
        draft: selectedContent.draft,
        target_keywords: selectedContent.target_keywords
      });
      fetchContent();
      setEditDialogOpen(false);
      toast.success('Content updated successfully');
    } catch (error) {
      toast.error('Failed to update content');
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await api.delete(`/content/${contentId}/`);
      setContent(content.filter(c => c.content_id !== contentId));
      toast.success('Content deleted successfully');
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleSubmitForApproval = async (contentId) => {
    try {
      await api.post(`/content/${contentId}/submit-for-approval/`);
      fetchContent();
      toast.success('Submitted for approval');
    } catch (error) {
      toast.error('Failed to submit for approval');
    }
  };

  const handleHumanize = async (item) => {
    if (!item.brief && !item.draft) {
      toast.error('No content to humanize');
      return;
    }
    setHumanizingId(item.content_id);
    humanizeTask.start();
    try {
      const res = await api.post('/ai/humanize-content', {
        content: unwrapMarkdown(item.draft || item.brief),
        content_type: item.content_type,
        tone: 'natural',
      });
      setHumanizedResult({ contentId: item.content_id, ...res.data });
      humanizeTask.complete(res.data);
    } catch {
      toast.error('Failed to humanize content');
      humanizeTask.fail('Failed to humanize');
    } finally {
      setHumanizingId(null);
    }
  };

  const handleAcceptHumanized = async () => {
    if (!humanizedResult) return;
    try {
      await api.put(`/content/${humanizedResult.contentId}/`, {
        draft: humanizedResult.humanized_content,
        status: 'humanized',
      });
      setContent(prev => prev.map(c =>
        c.content_id === humanizedResult.contentId
          ? { ...c, draft: humanizedResult.humanized_content, status: 'humanized' }
          : c
      ));
      setHumanizedResult(null);
      toast.success('Humanized version saved');
    } catch {
      toast.error('Failed to save humanized content');
    }
  };

  const handlePublishContent = async (contentId) => {
    setPublishing(contentId);
    try {
      await api.post(`/content/${contentId}/publish/`);
      fetchContent();
      toast.success('Content published!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(null);
    }
  };

  const handlePushToCms = async (contentId) => {
    setPublishing(contentId);
    try {
      const res = await api.post(`/content/${contentId}/push-to-cms/`);
      fetchContent();
      if (res.data?.published_url) {
        toast.success(<span>Published to website! <a href={res.data.published_url} target="_blank" rel="noopener noreferrer" className="underline font-semibold">View live</a></span>);
      } else {
        toast.success('Content pushed to CMS!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to CMS');
    } finally {
      setPublishing(null);
    }
  };

  const pipelineStats = {
    ideas: content.filter(c => c.status === 'draft').length,
    review: content.filter(c => c.status === 'ready_for_approval').length,
    approved: content.filter(c => c.status === 'approved').length,
    published: content.filter(c => c.status === 'published').length
  };

  return (
    <div className="space-y-8" data-testid="content-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1 text-slate-900 dark:text-white">Content Marketing</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage your content pipeline and publishing
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            className="gds-btn-primary" 
            onClick={() => setCreateDialogOpen(true)}
            data-testid="create-content-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Content
          </Button>
        </div>
      </div>

      {/* Pipeline Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { key: 'ideas', label: 'Drafts', count: pipelineStats.ideas, color: 'slate' },
          { key: 'review', label: 'In Review', count: pipelineStats.review, color: 'amber' },
          { key: 'approved', label: 'Approved', count: pipelineStats.approved, color: 'blue' },
          { key: 'published', label: 'Published', count: pipelineStats.published, color: 'emerald' },
        ].map((stage, i) => (
          <motion.div
            key={stage.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.1 }}
          >
            <Card className="gds-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{stage.label}</p>
                    <p className="text-3xl font-bold font-heading mt-1">{stage.count}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full bg-${stage.color}-500`} />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Content List */}
      <Card className="gds-card">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="font-heading flex items-center gap-2 text-slate-900 dark:text-white">
              <FileText className="w-5 h-5 text-indigo-500" />
              Content Library
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search content..."
                className="pl-9 w-64"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                data-testid="content-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={(v) => { setSelectedTab(v); setPage(1); }}>
            <TabsList className="mb-6 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
              <TabsTrigger value="all" className="rounded-lg">All</TabsTrigger>
              <TabsTrigger value="draft" className="rounded-lg">Drafts</TabsTrigger>
              <TabsTrigger value="ready_for_approval" className="rounded-lg">Review</TabsTrigger>
              <TabsTrigger value="published" className="rounded-lg">Published</TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              </div>
            ) : filteredContent.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No content found</p>
                <Button 
                  className="mt-4 gds-btn-primary"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Create Your First Content
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredContent.map((item, i) => (
                  <motion.div
                    key={item.content_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.05 }}
                    className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                    onClick={() => handleViewContent(item)}
                  >
                    {/* Pipeline Status Bar */}
                    <div className="mb-3">
                      <PipelineBar status={item.status} />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 dark:text-white">{item.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={getTypeBadge(item.content_type).color}>
                              {getTypeBadge(item.content_type).label}
                            </Badge>
                            {item.seo_score && (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                SEO: {item.seo_score}/100
                              </Badge>
                            )}
                            <span className="text-xs text-slate-500">
                              {item.target_keywords?.slice(0, 2).join(', ')}
                            </span>
                          </div>
                          {/* Content preview snippet */}
                          {(item.draft || item.brief) && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 line-clamp-2">
                              {unwrapMarkdown(item.draft || item.brief).replace(/[#*_`>\-\[\](){}":]/g, '').slice(0, 180)}
                              {(item.draft || item.brief).length > 180 ? '...' : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {/* Contextual action buttons */}
                        {(item.status === 'draft' || item.status === 'ai_generated' || item.status === 'humanized') && (
                          <Button variant="outline" size="sm"
                            disabled={humanizingId === item.content_id}
                            onClick={() => handleHumanize(item)}>
                            {humanizingId === item.content_id
                              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              : <Wand2 className="w-4 h-4 mr-1" />}
                            {humanizingId === item.content_id ? 'Humanizing...' : 'Humanize'}
                          </Button>
                        )}
                        {item.status === 'humanized' && (
                          <Button variant="outline" size="sm" onClick={() => handleSubmitForApproval(item.content_id)}>
                            <Send className="w-4 h-4 mr-1" /> Submit for Approval
                          </Button>
                        )}
                        {item.status === 'ready_for_approval' && (
                          <Badge className="bg-amber-100 text-amber-700">Pending...</Badge>
                        )}
                        {item.status === 'approved' && (
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              disabled={publishing === item.content_id}
                              onClick={() => handlePushToCms(item.content_id)}>
                              {publishing === item.content_id
                                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                : <ExternalLink className="w-4 h-4 mr-1" />}
                              Publish to Website
                            </Button>
                            <Button size="sm" variant="outline"
                              disabled={publishing === item.content_id}
                              onClick={() => handlePublishContent(item.content_id)}>
                              Mark Published
                            </Button>
                          </div>
                        )}
                        {item.status === 'published' && item.published_url && (
                          <a 
                            href={item.published_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                            title="View Published Page"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}

                        <Badge className={getStatusBadge(item.status).color}>
                          {getStatusBadge(item.status).label}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewContent(item)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditContent(item)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {(item.status === 'draft' || item.status === 'ai_generated') && (
                              <DropdownMenuItem onClick={() => handleSubmitForApproval(item.content_id)}>
                                <Send className="w-4 h-4 mr-2" />
                                Submit for Review
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => handleDeleteContent(item.content_id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </Tabs>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Page {page} of {Math.ceil(total / pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Content Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900 dark:text-white">Create New Content</DialogTitle>
          </DialogHeader>
          {/* Example filler */}
          <div className="flex items-center gap-2 px-1 pb-1">
            <button
              type="button"
              onClick={() => {
                const ex = DUMMY_EXAMPLES[exampleIdx % DUMMY_EXAMPLES.length];
                setNewContent(ex);
                setGeneratedContent(null);
                generateTask.set({ result: null });
                setExampleIdx(i => i + 1);
                toast.info(`Example ${(exampleIdx % DUMMY_EXAMPLES.length) + 1} of ${DUMMY_EXAMPLES.length} loaded`);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/30 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Fill with Example {exampleIdx % DUMMY_EXAMPLES.length + 1}/{DUMMY_EXAMPLES.length}
            </button>
            {(newContent.title || newContent.target_keywords || newContent.brief) && (
              <button
                type="button"
                onClick={() => {
                  setNewContent({ title: '', content_type: 'blog', target_keywords: '', brief: '' });
                  setGeneratedContent(null);
                  generateTask.set({ result: null });
                }}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
              <Input
                id="title"
                placeholder="Enter content title"
                value={newContent.title}
                onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
                data-testid="content-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Content Type</Label>
              <select
                id="type"
                className="w-full h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950"
                value={newContent.content_type}
                onChange={(e) => setNewContent({ ...newContent, content_type: e.target.value })}
                data-testid="content-type-select"
              >
                <option value="blog">Blog Post</option>
                <option value="article">Article</option>
                <option value="landing_page">Landing Page</option>
                <option value="gbp_post">GBP Post</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="keywords">Target Keywords <span className="text-red-500">*</span></Label>
              <Input
                id="keywords"
                placeholder="seo, marketing, digital (comma separated)"
                value={newContent.target_keywords}
                onChange={(e) => setNewContent({ ...newContent, target_keywords: e.target.value })}
                data-testid="content-keywords-input"
              />
            </div>
            
            {!generatedContent || generating ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="brief">Brief / Outline (optional)</Label>
                  <Textarea
                    id="brief"
                    placeholder="Enter content brief or outline..."
                    rows={4}
                    value={newContent.brief}
                    onChange={(e) => setNewContent({ ...newContent, brief: e.target.value })}
                    data-testid="content-brief-input"
                    disabled={generating}
                  />
                </div>
                
                <div className="flex items-center justify-center py-4">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleGenerateAIContent}
                    disabled={generating || !newContent.title || !newContent.target_keywords}
                    data-testid="generate-ai-content-btn"
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {generating ? 'Generating content, please wait...' : 'Generate with AI'}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2 text-slate-900 dark:text-white">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    AI Generated Content
                  </h4>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <Target className="w-3 h-3 mr-1" />
                      SEO Score: {generatedContent.seo_score}/100
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => { setGeneratedContent(null); generateTask.set({ result: null }); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="prose prose-base max-w-none dark:prose-invert max-h-[32rem] overflow-y-auto p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5">
                  <ReactMarkdown>{unwrapMarkdown(generatedContent.content)}</ReactMarkdown>
                </div>
                {generatedContent.meta_description && (
                  <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                    <p className="text-xs font-medium text-indigo-600 mb-1">Meta Description</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{generatedContent.meta_description}</p>
                  </div>
                )}

                {/* Humanize button for generated content */}
                {!generatedContent.humanized && (
                  <Button variant="outline" className="w-full"
                    disabled={generating || !!humanizingId}
                    onClick={async () => {
                      setHumanizingId('create-dialog');
                      humanizeTask.start();
                      try {
                        const res = await api.post('/ai/humanize-content', {
                          content: generatedContent.content,
                          content_type: newContent.content_type,
                          tone: 'natural',
                        });
                        setGeneratedContent(prev => ({
                          ...prev,
                          humanized: res.data.humanized_content,
                          humanize_summary: res.data.changes_summary,
                          naturalness_score: res.data.naturalness_score,
                        }));
                        toast.success(`Humanized! Naturalness: ${res.data.naturalness_score}/100`);
                        humanizeTask.complete(res.data);
                      } catch {
                        toast.error('Humanization failed');
                        humanizeTask.fail('Humanization failed');
                      } finally {
                        setHumanizingId(null);
                      }
                    }}>
                    {humanizingId === 'create-dialog' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4 mr-2" />
                    )}
                    {humanizingId === 'create-dialog' ? 'Humanizing...' : 'Humanize Content'}
                  </Button>
                )}

                {/* Humanized comparison */}
                {generatedContent.humanized && (
                  <div className="space-y-3 border-t border-slate-200 dark:border-slate-700 pt-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium flex items-center gap-2 text-slate-900 dark:text-white">
                        <Wand2 className="w-4 h-4 text-violet-500" />
                        Humanized Version
                      </h4>
                      <Badge className="bg-violet-100 text-violet-700">
                        Naturalness: {generatedContent.naturalness_score}/100
                      </Badge>
                    </div>
                    {generatedContent.humanize_summary && (
                      <p className="text-xs text-slate-500 italic">{generatedContent.humanize_summary}</p>
                    )}
                    <div className="prose prose-base max-w-none dark:prose-invert max-h-[24rem] overflow-y-auto p-4 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5">
                      <ReactMarkdown>{generatedContent.humanized}</ReactMarkdown>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white flex-1"
                        onClick={() => {
                          setGeneratedContent(prev => {
                            const { humanized, humanize_summary, naturalness_score, ...rest } = prev;
                            return { ...rest, content: prev.humanized, was_humanized: true };
                          });
                          toast.success('Humanized version applied');
                        }}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Use Humanized Version
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1"
                        onClick={() => {
                          setGeneratedContent(prev => {
                            const { humanized, humanize_summary, naturalness_score, ...rest } = prev;
                            return rest;
                          });
                          toast.info('Keeping original content');
                        }}>
                        Keep Original
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setCreateDialogOpen(false);
              setGeneratedContent(null);
              generateTask.set({ result: null });
              setNewContent({ title: '', content_type: 'blog', target_keywords: '', brief: '' });
              setExampleIdx(0);
            }}>
              Cancel
            </Button>
            {generatedContent ? (
              <Button className="gds-btn-primary" onClick={handleSaveGeneratedContent} disabled={saving} data-testid="save-generated-btn">
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                {saving ? 'Saving...' : 'Save Content'}
              </Button>
            ) : (
              <Button className="gds-btn-primary" onClick={handleCreateContent} data-testid="submit-content-btn">
                Create Content
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900 dark:text-white">{selectedContent?.title}</DialogTitle>
          </DialogHeader>
          {selectedContent && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={getTypeBadge(selectedContent.content_type).color}>
                  {getTypeBadge(selectedContent.content_type).label}
                </Badge>
                <Badge className={getStatusBadge(selectedContent.status).color}>
                  {getStatusBadge(selectedContent.status).label}
                </Badge>
                {selectedContent.seo_score && (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    SEO: {selectedContent.seo_score}/100
                  </Badge>
                )}
              </div>
              
              {selectedContent.target_keywords?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Target Keywords</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedContent.target_keywords.map((kw, i) => (
                      <Badge key={i} variant="outline">{kw}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Show content body: prefer draft, fall back to brief */}
              {(selectedContent.draft || selectedContent.brief) ? (
                <div className="space-y-4">
                  {selectedContent.draft && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-500" />
                        {selectedContent.brief ? 'Final Draft' : 'Content'}
                      </p>
                      <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 prose prose-base max-w-none dark:prose-invert max-h-[50vh] overflow-y-auto prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5">
                        <ReactMarkdown>{unwrapMarkdown(selectedContent.draft)}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {selectedContent.brief && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-violet-500" />
                        {selectedContent.draft ? 'Original / AI Generated' : 'Content'}
                      </p>
                      <div className={`p-4 rounded-xl border prose prose-base max-w-none dark:prose-invert max-h-[50vh] overflow-y-auto prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5 ${
                        selectedContent.draft
                          ? 'bg-slate-50/50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-800'
                          : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                      }`}>
                        <ReactMarkdown>{unwrapMarkdown(selectedContent.brief)}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No content body yet. Edit this item to add content.</p>
                </div>
              )}

              {selectedContent.published_url && (
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <p className="text-xs font-medium text-emerald-600 mb-1">Published URL</p>
                  <a href={selectedContent.published_url} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-emerald-700 hover:underline flex items-center gap-1">
                    {selectedContent.published_url}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900 dark:text-white">Edit Content</DialogTitle>
          </DialogHeader>
          {selectedContent && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={selectedContent.title}
                  onChange={(e) => setSelectedContent({ ...selectedContent, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Content / Brief</Label>
                <Textarea
                  rows={8}
                  value={selectedContent.brief || ''}
                  onChange={(e) => setSelectedContent({ ...selectedContent, brief: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Draft</Label>
                <Textarea
                  rows={8}
                  value={selectedContent.draft || ''}
                  onChange={(e) => setSelectedContent({ ...selectedContent, draft: e.target.value })}
                  placeholder="Write your draft content here..."
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="gds-btn-primary" onClick={handleUpdateContent}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Humanizer Comparison Dialog */}
      <Dialog open={!!humanizedResult} onOpenChange={(open) => { if (!open) setHumanizedResult(null); }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900 dark:text-white flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-violet-500" />
              Content Humanizer
            </DialogTitle>
          </DialogHeader>
          {humanizedResult && (
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{humanizedResult.changes_summary}</p>
                <Badge className="bg-violet-100 text-violet-700">
                  Naturalness: {humanizedResult.naturalness_score}/100
                </Badge>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase mb-2">Original</p>
                  <div className="prose prose-base max-w-none dark:prose-invert p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 max-h-80 overflow-y-auto prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5">
                    <ReactMarkdown>{humanizedResult.original_content}</ReactMarkdown>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-violet-500 uppercase mb-2">Humanized</p>
                  <div className="prose prose-base max-w-none dark:prose-invert p-4 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 max-h-80 overflow-y-auto prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-li:my-0.5">
                    <ReactMarkdown>{humanizedResult.humanized_content}</ReactMarkdown>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button className="bg-violet-600 hover:bg-violet-700 text-white flex-1" onClick={handleAcceptHumanized}>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Use Humanized Version
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setHumanizedResult(null)}>
                  Keep Original
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
